/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {Signal as SignalType, signal} from '@preact/signals-react';
import {error, warn} from '@ion/core/src/logger/logger';

/**
 * Defines the data type for a Signal's mutator function which allows for
 * specifying the type of the signal's `value`.
 *
 * @template `T` The provided signal's `value` data type.
 */
export type ValueProp<T> = SignalType & {
  value: T;
};

/**
 * Provides a utility for setting the value of a `Signal` via a dedicated mutator
 * function. The `createMutator` function is intended to reduce the boilerplate
 * required when creating signal mutator functions for each corresponding `Signal`.
 *
 * As per our iOn Monorepo Best Practices and Conventions for Preact Signals, each
 * `Signal` is required to implement a dedicated mutator function for setting the
 * signal's `value`. This can become quite repetitive and unnecessarily adds to the
 * total LOC (line of code) of the given store. Accordingly, the `createMutator`
 * utility function can be used to create dedicated mutation functions without the
 * need to explicitly implement them.
 *
 * ```typescript
 *  // /packages/<your-app>/src/<your-feature>/store.ts
 *  import {createMutator} from '@ion/applications/src/store/SignalUtils';
 *  ...
 *
 *  // create a signal ...
 *  export const firstName = signal<string>();
 *
 *  // create a mutator function for the signal ...
 *  export const setFirstName = createMutator<string>(firstName);
 *  ...
 *
 *
 *  // /packages/<your-app>/src/<your-feature>/FirstName.tsx
 *  import {firstName, setFirstName} from './store';
 *  ...
 *
 *  export const FirstName = () => (
 *    <div>
 *      <input type="text"
 *        value={firstName.value}
 *        onChange={function(e) {
 *          setFirstName(e.target.value)
 *        }}
 *      />
 *    </div>
 *  );
 * ```
 *
 * For additional information on the Signals Best Practices and Standards, see the
 * [Official Documentation](https://tinyurl.com/2dy59x76).
 *
 * @param    `signal` The `Signal` for which a mutator function is to be created.
 * @template `T` An optional type of the signal's `value`, this ensures that the
 *           signals mutator function is type-safe.
 * @returns  A dedicated mutator function for the provided `signal`, the mutator
 *           is a function which can be used exclusively by clients to set the
 *           signal's `value`. This ensure mutations (changes) to the value of
 *           a Signal are encapsulated and can easily be tracked.
 */
export const createMutator = <T>(signal: ValueProp<T>) => {
  if (signal) {
    return (value: T) => {
      signal.value = value;
    };
  } else {
    error('Bypassing Mutator Creation as the Signal provided to createMutator is Nil');
  }
};
